// Ubicación en el repo: src/pages/SetPassword.jsx
// Pantalla a la que llega el link del correo de invitación o de
// restablecimiento de contraseña. Acá solo se pide la nueva contraseña y se
// llama a supabase.auth.updateUser().
//
// A propósito NO está envuelta en RequireAuth/RedirectIfAuthed (ver
// App.jsx): esa lógica asume "sesión = login normal ya resuelto", y acá el
// tipo de sesión es distinto (viene de un link de un solo uso).
//
// Bug real encontrado (DECISIONS.md #25): si quien abre el link YA tenía
// otra sesión activa en el mismo navegador (ej. un Admin probando la
// invitación de otro profesional en la misma pestaña/navegador donde tiene
// su propia sesión abierta), la contraseña nueva puede terminar
// aplicándose a la sesión vieja en vez de a la del link. Por eso acá abajo
// se muestra explícitamente el correo de la sesión activa ANTES de guardar
// — si no coincide con el profesional esperado, no hay que enviar el
// formulario.
//
// DECISIONS.md #58 — cómo llega la sesión hoy: `token_hash` + `type` como
// query params en la URL (no el link directo de Supabase a `/auth/v1/verify`
// que se usaba antes). Ese link viejo quedaba vulnerable a que un escaneo
// automático de correo (Gmail u otro filtro antiphishing) le hiciera un GET
// antes que el usuario real y quemara el token de un solo uso — el usuario
// después veía "link ya usado" sin haberlo tocado. Acá el token se consume
// recién cuando ESTA página llama a verifyOtp(): nadie puede gatillarlo con
// solo pre-visitar un link, porque el correo ya no contiene ningún link
// directo a Supabase, solo a esta página de la app.
//
// DECISIONS.md #59 — paso de MFA para quien ya tiene 2FA activo: un link de
// tipo `recovery` (o `invite`, si alguna vez se reenvía a alguien que ya
// activó 2FA) deja una sesión aal1, sea cual sea el mecanismo con que se
// estableció. Si esa cuenta ya tiene un factor TOTP verificado, Supabase
// exige aal2 para `updateUser({password})` y lo rechaza con "AAL2 session
// is required" — no es un bug de este fix, es el comportamiento normal de
// Supabase, que este flujo nunca había contemplado (nadie lo había probado
// todavía con una cuenta con 2FA real). Se resuelve igual que en Login.jsx:
// si `mfaSatisfied` es falso, se pide el código de 6 dígitos ANTES de
// mostrar el formulario de contraseña, con la misma `verifyMfaChallenge()`.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { verifyMfaChallenge, logLoginEvent } from '../lib/auth';
import { useSession } from '../lib/SessionContext';
import PasswordField from '../components/PasswordField';
import Spinner from '../components/Spinner';

export default function SetPassword() {
  const { status, session, mfaSatisfied } = useSession();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  async function handleMfaSubmit(e) {
    e.preventDefault();
    setMfaError('');
    setMfaLoading(true);
    try {
      await verifyMfaChallenge(mfaCode.trim());
      // Sube el aal de la sesión actual; SessionContext lo recalcula solo
      // vía onAuthStateChange y mfaSatisfied pasa a true.
    } catch (err) {
      setMfaError(err.message || 'Código incorrecto.');
    } finally {
      setMfaLoading(false);
    }
  }

  const verifyAttempted = useRef(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    if (verifyAttempted.current) return;
    const tokenHash = searchParams.get('token_hash');
    const type = searchParams.get('type');
    if (!tokenHash || !type) return; // sin estos params, no hay nada que verificar acá
    verifyAttempted.current = true;
    setVerifying(true);
    supabase.auth
      .verifyOtp({ token_hash: tokenHash, type })
      .then(({ error: verifyErr }) => {
        if (verifyErr) {
          setVerifyError('Este link no es válido o ya expiró. Pide que te envíen uno nuevo.');
        }
      })
      .finally(() => setVerifying(false));
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      // Registrar el éxito acá (y no solo en login() normal) es lo que le
      // permite a check_login_access() "olvidar" los fallos que provocaron
      // el bloqueo — bug real encontrado 19-ago-2026: sin este registro, la
      // cuenta seguía viéndose bloqueada incluso ya con la contraseña
      // nueva y correcta, mientras los fallos viejos siguieran dentro de la
      // ventana de 15 min (ver migración 0028). Best-effort: si esto falla,
      // no debe impedir que la persona entre igual.
      await logLoginEvent(true).catch(() => {});
      setDone(true);
      // '/app', no '/' (23-ago-2026, ver DECISIONS.md): desde que existe
      // PublicLanding en la raíz, mandar acá a la persona que recién definió
      // su contraseña la dejaría varada en la página pública en vez de
      // entrarla al software.
      setTimeout(() => navigate('/app', { replace: true }), 1200);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la contraseña.');
    } finally {
      setLoading(false);
    }
  }

  if (status === 'loading' || verifying) {
    return (
      <div className="app-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <img src="/agendadock-logo.png" alt="AgendaDock" className="auth-card__logo" />
        <h1>Agenda Comunal</h1>
        <p className="auth-subtitle">by GlobalVision</p>

        {verifyError || !session ? (
          <p className="auth-error">
            {verifyError || 'Este link no es válido o ya expiró. Pide que te envíen uno nuevo.'}
          </p>
        ) : done ? (
          <p>Contraseña guardada. Entrando…</p>
        ) : !mfaSatisfied ? (
          <form onSubmit={handleMfaSubmit}>
            <p className="auth-subtitle" style={{ margin: '0 0 16px' }}>
              Esta cuenta tiene verificación en dos pasos activada
            </p>
            <p className="field-hint" style={{ marginBottom: 16 }}>
              Ingresa el código de tu app autenticadora para poder definir la nueva contraseña de{' '}
              <strong>{session.user.email}</strong>.
            </p>
            <label className="field">
              <span>Código (6 dígitos)</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoFocus
                required
              />
            </label>
            {mfaError && <p className="auth-error">{mfaError}</p>}
            <button type="submit" disabled={mfaLoading}>
              {mfaLoading ? 'Verificando…' : 'Verificar'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="auth-subtitle" style={{ margin: '0 0 16px' }}>
              Define tu contraseña
            </p>
            <p className="field-hint" style={{ marginBottom: 16 }}>
              Vas a definir la contraseña para: <strong>{session.user.email}</strong>
              <br />
              Si este NO es el correo que esperabas, no sigas — cierra esta pestaña y pide que te reenvíen el
              link en una ventana de incógnito, sin otra sesión abierta en el mismo navegador.
            </p>
            <label className="field">
              <span>Nueva contraseña</span>
              <PasswordField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </label>
            <label className="field">
              <span>Repite la contraseña</span>
              <PasswordField
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={loading}>
              {loading ? 'Guardando…' : 'Guardar contraseña'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
